import React, { useReducer } from 'react'
import DefaultLayout, {
  LayoutSpacingOptions,
} from '@src/components/DefaultLayout'
import Breadcrumb from '@src/components/Breadcrumb'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import { shrnaRoute, useRouter, homeRoute } from '@src/routes'
import { makeStyles, Theme } from '@material-ui/core/styles'
import PageHeader from '../../../Configurators/components/PageHeader'
import LeftNav from '../../../SemiConfigurators/components/LeftNav'
import { useUserSession } from '@utils/useUserSession'
import { initialState, reducer, ContextProps } from './Reducer'
import { ActiveLinkType, SemiConfigProperties } from './Enum'
import { FormattedMessage } from 'react-intl'
import messages from '@semi-configurator-messages'
import ProductSearchTab from '@src/routes/SemiConfigurators/components/ProductSearchTab'
import ManualSearch from './Step1/ManualSearch'
import UploadQueries from './Step1/UploadQueries'
import Step2 from './Step2'
import Step3 from './Step3'
import Step4 from './Step4'
import ErrorAlert from '../../../Configurators/components/ErrorAlert/ErrorAlert'
import { useCurrentUserQuery } from '@src/queries/CurrentUserQuery.generated'
import { default as UUID } from 'uuid'
import ConfiguratorPageError from '@src/routes/Configurators/utils/ConfiguratorPageError'
import { getProductStatus } from '@src/routes/Configurators/utils/util'
import { BreadcrumbNavigationUrls } from '@src/routes/Configurators/utils/BreadcrumbNavigationUrlsEnum'
import { useGetProductInformationQuery } from '@src/queries/ConfiguratorTubesQuery.generated'

const useStyles = makeStyles((theme: Theme) => ({
  headerPadLeft: {
    paddingLeft: theme.spacing(4.5),
  },
}))

const validateActiveLink = (activeLink) => {
  if (activeLink === '' || activeLink == null) {
    return ActiveLinkType.ProductSearch
  } else if (activeLink === ActiveLinkType.SelectClones) {
    return ActiveLinkType.SelectClones
  } else {
    return activeLink
  }
}
export const semiConfigContext = React.createContext({} as ContextProps)

export interface ProductDetails {
  productType: string
  participantId: string
  countryCode: string
  isRegUser: boolean
  locale: string
  isCSR: boolean
  isCSRSupervisor: boolean
  sapNextEnabled: boolean
  cartId: string
  cartItemId: string
  region: string
  timeZone: string
  store: string
}

const Shrna = () => {
  const classes = useStyles()
  const [state, dispatch] = useReducer(reducer, initialState)
  const router = useRouter()
  const { userSession } = useUserSession()
  const countryCd = userSession.country
  const [openError, setOpenError] = React.useState(false)
  const [activeStep, setActiveStep] = React.useState(0)
  const [productCode, setProductCode] = React.useState<number>(0)
  const [currencyFormat, setCurrencyFormat] = React.useState<string>('')
  const [decimalSeparator, setDecimalSeparator] = React.useState<any>('')
  const [prefixCurrecny, setPrefixCurrecny] = React.useState<any>(true)
  const [thousandSeparator, setThousandSeparator] = React.useState<any>('')
  const [thousandSpacing, setthousandSpacing] = React.useState<any>('')
  let productInformationResponse: any = {}
  const [enableSelectClonesLink, setEnableSelectClonesLink] =
    React.useState(false)
  const [enableSpecificationsLink, setEnableSpecificationsLink] =
    React.useState(false)
  const [enableReviewOrderLink, setEnableReviewOrderLink] =
    React.useState(false)

  React.useEffect(() => {
    dispatch({ type: 'INIT' })
    const url = `${shrnaRoute.index()}?activeLink=${
      ActiveLinkType.ProductSearch
    }`
    router.push(url)
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data, loading } = useCurrentUserQuery()

  const currentUser: any = data?.me || null
  const loggedIn =
    !loading &&
    currentUser &&
    currentUser.__typename === SemiConfigProperties.LoggedInUser

  let participationId: string
  let locale: string
  let isCSR: boolean
  let isCSRSupervisor: boolean
  let isSAPNextEnabled: boolean
  let isOrderCustomer: boolean
  let isOrderableCustomer: boolean
  let isGuestShoppingCountry: boolean
  let isConfiguratorDisabled: boolean
  const date = new Date()
  const timeZone = (date.getTimezoneOffset() / 60) * -1
  let product = ''
  let cartId = ''
  let itemId = ''

  product = ActiveLinkType.ProductName

  if (router !== null && router.query !== null) {
    cartId = router.query.cartid
    itemId = router.query.itemid
  }

  let accessToken = ''
  if (userSession.accessToken) {
    accessToken = userSession.accessToken
  }
  let store = ''
  if (userSession.accessToken) {
    store = userSession.store
  }
  if (loggedIn && currentUser) {
    participationId = currentUser.id
    locale = `${userSession.language}_${countryCd}`
    if (locale === '') {
      locale = currentUser?.metadata.locale
    }
    isCSR = currentUser?.roles.isCustomerServiceRep
    isCSRSupervisor = currentUser?.roles.isCustomProductCSR
    isSAPNextEnabled = currentUser.metadata.isSAPNextEnabled
    isGuestShoppingCountry = currentUser.metadata.guestShoppingType
    isOrderCustomer =
      currentUser?.roles.isPublicOrderingCustomer ||
      currentUser?.roles.isQuoteRequester
    if (isGuestShoppingCountry !== null) {
      isOrderableCustomer = true
    } else if (isOrderCustomer && isGuestShoppingCountry === null) {
      isOrderableCustomer = true
    } else {
      isOrderableCustomer = false
    }
    isConfiguratorDisabled =
      currentUser.participantSitePreference?.hideOligoPeptideDesign
  } else if (userSession.accessToken) {
    participationId = userSession.accessToken
    locale = `${userSession.language}_${countryCd}`
    isCSR = false
    isCSRSupervisor = false
    isOrderableCustomer = false
    isSAPNextEnabled = currentUser?.metadata.isSAPNextEnabled
    isConfiguratorDisabled = false
  } else {
    //TODO: commented participationId as a part of fix for STRAT-11633
    //Identify why the page is going in an infinite loop when fetching the id dynamically.
    participationId = UUID.v4()
    locale = `en_${countryCd}`
    isCSR = false
    isCSRSupervisor = false
    isSAPNextEnabled = true
    isOrderableCustomer = false
    isConfiguratorDisabled = false
  }

  React.useEffect(() => {
    if (state.pageError.length === 0) {
      setOpenError(false)
    } else {
      setOpenError(true)
    }
  }, [state.pageError])

  const productDetails: ProductDetails = {
    productType: product,
    participantId: participationId,
    countryCode: countryCd,
    isRegUser: loggedIn,
    locale: locale,
    isCSR: isCSR,
    isCSRSupervisor: isCSRSupervisor,
    sapNextEnabled: isSAPNextEnabled,
    cartId: cartId,
    cartItemId: itemId,
    region: countryCd,
    timeZone: timeZone.toString(),
    store: store,
  }

  const productInput = {
    productType: ActiveLinkType.ProductName,
    countryCode: countryCd,
    isCsr: isCSR,
    isCsrSupervisor: isCSRSupervisor,
    isOrderableCustomer: isOrderableCustomer,
    regUser: loggedIn,
    locale: locale,
    sapNextEnabled: isSAPNextEnabled,
    hideOligoPeptideDesign: isConfiguratorDisabled,
  }

  const { data: productInformationData } = useGetProductInformationQuery({
    variables: {
      input: productInput,
    },
    fetchPolicy: 'no-cache',
    skip: !accessToken,
    ssr: false,
  })

  React.useEffect(() => {
    if (loggedIn !== undefined && productInformationData) {
      const productInformation = productInformationData.getProductInformation
      if (productInformation?.displayName) {
        productInformationResponse = productInformation
        const productStatus = getProductStatus(
          productInformationResponse.displayName
        )
        setProductCode(productStatus)
      }
      if (productInformation?.currencyFormat) {
        setCurrencyFormat(productInformation.currencyFormat)
      }
      setDecimalSeparator(productInformation?.decimalSeparator)
      setPrefixCurrecny(productInformation?.prefixCurrecny)
      setThousandSeparator(productInformation?.thousandSeparator)
      setthousandSpacing(productInformation?.thousandSpacing)
    }
  }, [productInformationData])

  React.useEffect(() => {
    setActiveStep(state.stepNo)
  }, [state.stepNo])

  React.useEffect(() => {
    if (!state.searchValue.term) {
      dispatch({ type: 'SET_STEP_NO', data: 0 })
      setEnableSelectClonesLink(true)
      setEnableSpecificationsLink(true)
      setEnableReviewOrderLink(true)
    }
  }, [router.query.activeLink])

  React.useEffect(() => {
    if (router) {
      const { activeLink } = router.query
      if (
        (state.stepNo === 0 && activeLink === ActiveLinkType.SelectClones) ||
        (state.stepNo === 1 &&
          activeLink === ActiveLinkType.SelectClones &&
          state.traceOnClick) ||
        (state.stepNo === 2 &&
          activeLink === ActiveLinkType.SelectClones &&
          state.traceOnClick) ||
        (state.stepNo === 3 &&
          activeLink === ActiveLinkType.SelectClones &&
          state.traceOnClick)
      ) {
        dispatch({ type: 'SET_STEP_NO', data: 1 })
        setEnableSelectClonesLink(false)
        setEnableSpecificationsLink(true)
        setEnableReviewOrderLink(true)
      } else if (
        (state.stepNo === 1 && activeLink === ActiveLinkType.Specifications) ||
        (state.stepNo === 3 &&
          activeLink === ActiveLinkType.Specifications &&
          state.traceOnClick) ||
        (state.stepNo === 2 &&
          activeLink === ActiveLinkType.SelectClones &&
          state.traceOnClick)
      ) {
        dispatch({ type: 'SET_STEP_NO', data: 2 })
        setEnableSelectClonesLink(false)
        setEnableSpecificationsLink(false)
        setEnableReviewOrderLink(true)
      } else if (
        (state.stepNo === 2 &&
          activeLink === ActiveLinkType.ReviewAndConfirm) ||
        (activeLink === ActiveLinkType.Specifications &&
          state.stepNo === 3 &&
          state.traceOnClick) ||
        (activeLink === ActiveLinkType.SelectClones &&
          state.stepNo === 3 &&
          state.traceOnClick)
      ) {
        dispatch({ type: 'SET_STEP_NO', data: 3 })
        setEnableSelectClonesLink(false)
        setEnableSpecificationsLink(false)
        setEnableReviewOrderLink(false)
      } else if (
        (state.stepNo === 0 && activeLink === ActiveLinkType.ProductSearch) ||
        (state.stepNo === 1 &&
          activeLink === ActiveLinkType.ProductSearch &&
          state.traceOnClick) ||
        (state.stepNo === 2 &&
          activeLink === ActiveLinkType.ProductSearch &&
          state.traceOnClick) ||
        (state.stepNo === 3 &&
          activeLink === ActiveLinkType.ProductSearch &&
          state.traceOnClick) ||
        (state.stepNo === 3 &&
          activeLink === ActiveLinkType.ReviewAndConfirm &&
          state.traceOnClick)
      ) {
        dispatch({ type: 'SET_STEP_NO', data: 0 })
        setEnableSelectClonesLink(true)
        setEnableSpecificationsLink(true)
        setEnableReviewOrderLink(true)
      }
    }
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.activeLink, state.traceOnClick])

  if (!router || !router.query) return null
  let { activeLink = '' } = router.query
  activeLink = validateActiveLink(activeLink)

  const breadcrumbItems = [
    {
      url: homeRoute.index(),
      label: <FormattedMessage {...messages.HOME} />,
    },
    {
      url: BreadcrumbNavigationUrls.Custom_Products_shrna,
      label: <FormattedMessage {...messages.SEMICONF_BREADCRUMB1_shrna} />,
    },
    {
      url: BreadcrumbNavigationUrls.Oligos_Probes_shrna,
      label: <FormattedMessage {...messages.SEMICONF_BREADCRUMB2_shrna} />,
    },
    {
      label: (
        <FormattedMessage
          id="SEMICONF_TITLE_PREDESIGNED_shrna"
          defaultMessage="Predesigned shRNA"
        />
      ),
    },
  ]

  const sidebarItems = [
    {
      label: (
        <FormattedMessage {...messages.SEMICONF_SEARCH_GENESORCLONES_shrna} />
      ),
      activeLink: activeLink === ActiveLinkType.ProductSearch ? true : false,
      url: `${shrnaRoute.index()}?activeLink=${ActiveLinkType.ProductSearch}`,
      linkDisable: false,
      productName: ActiveLinkType.ProductName,
    },
    {
      label: <FormattedMessage {...messages.CONF_ITEM_SELECTION} />,
      activeLink: activeLink === ActiveLinkType.SelectClones ? true : false,
      url: `${shrnaRoute.index()}?activeLink=${ActiveLinkType.SelectClones}`,
      linkDisable: enableSelectClonesLink,
      productName: ActiveLinkType.ProductName,
    },
    {
      label: <FormattedMessage {...messages.CONF_SPECIFICATIONS_crisprgrna} />,
      activeLink: activeLink === ActiveLinkType.Specifications ? true : false,
      url: `${shrnaRoute.index()}?activeLink=${ActiveLinkType.Specifications}`,
      linkDisable: enableSpecificationsLink,
      productName: ActiveLinkType.ProductName,
    },
    {
      label: <FormattedMessage {...messages.CONF_REVIEW_ORDER_crisprgrna} />,
      activeLink: activeLink === ActiveLinkType.ReviewAndConfirm ? true : false,
      url: `${shrnaRoute.index()}?activeLink=${
        ActiveLinkType.ReviewAndConfirm
      }`,
      linkDisable: enableReviewOrderLink,
      productName: ActiveLinkType.ProductName,
    },
  ]

  let shrnaContentComponent: React.ReactNode = (
    <>
      <ProductSearchTab
        manualSearch={<ManualSearch productDetails={productDetails} />}
        uploadQueries={<UploadQueries productDetails={productDetails} />}
      />
    </>
  )

  if (activeLink === ActiveLinkType.SelectClones)
    shrnaContentComponent = <Step2 productDetails={productDetails} />

  if (activeLink === ActiveLinkType.Specifications)
    shrnaContentComponent = <Step3 productDetails={productDetails} />
  if (activeLink === ActiveLinkType.ReviewAndConfirm)
    shrnaContentComponent = (
      <Step4
        productDetails={productDetails}
        currencyFormat={currencyFormat}
        thousandSeparator={thousandSeparator}
        thousandSpacing={thousandSpacing}
        prefixCurrecny={prefixCurrecny}
        decimalSeparator={decimalSeparator}
      />
    )

  return (
    <semiConfigContext.Provider value={{ state, dispatch }}>
      <DefaultLayout spacing={LayoutSpacingOptions.MD}>
        {productCode !== 0 ? (
          <ConfiguratorPageError
            productCode={productCode}
            country={countryCd}
          />
        ) : (
          <Container>
            <Breadcrumb items={breadcrumbItems} />
            <div className={classes.headerPadLeft}>
              <PageHeader
                label={
                  <FormattedMessage
                    id="SEMICONF_TITLE_PREDESIGNED_shrna"
                    defaultMessage="Predesigned shRNA"
                  />
                }
              />
            </div>
            <Grid container spacing={10}>
              <Grid item xs={3}>
                <LeftNav
                  sidebarItems={sidebarItems}
                  productName={ActiveLinkType.ProductName}
                  count={null}
                  price={null}
                  steps={''}
                  activeStep={activeStep}
                  subtotal={false}
                  productDetails={productDetails}
                  geneSearchError
                />
              </Grid>
              <Grid item xs={9}>
                <ErrorAlert
                  open={openError}
                  setOpen={setOpenError}
                  message={state.pageError}
                />
                <div>{shrnaContentComponent}</div>
              </Grid>
            </Grid>
          </Container>
        )}
      </DefaultLayout>
    </semiConfigContext.Provider>
  )
}

export default Shrna
